import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { toast } from "sonner";
import { Menu, X, ArrowUpRight, Heart, Building2, Hotel, Sun } from "lucide-react";
import SmoothScroll from "@/components/site/SmoothScroll";
import { FadeUp } from "@/components/site/Reveal";

/* ---------- assets ---------- */
const GEN = "https://static.prod-images.emergentagent.com/jobs/6918af59-ab46-445b-a012-e513807d76c0/images/";
const HERO = `${GEN}56c715b16b6dd4cc86035e6b7069c4c27a6edee8d756b5d131624e6fa3b87b3e.jpeg`;
const BURRATA = `${GEN}30bfe8675224d8b3aba6db0edeffe794c91e08bf5a5448cf0bf1e248de2964b0.jpeg`;
const CAPRESE = `${GEN}fd6f31059d7e5211d31e08bb35dc4d029ef4c9c2bd2e50779ccb4b59570e23e4.jpeg`;
const STILL = `${GEN}ba552b00557dd69c8905e18147ed239d0cc77234ffe8ab7d00acb96028cb4c55.jpeg`;
const SEA1 = "https://images.unsplash.com/photo-1607614372586-72d399dc57b6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const SEA2 = "https://images.unsplash.com/photo-1651835060162-64bebc55b425?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const SEA3 = "https://images.unsplash.com/photo-1586153464906-c4c3833ebe1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const ART = "https://customer-assets-gfyr7b9c.emergentagent.net/job_mozzarella-show/artifacts/";
const EV_VIDEO = `${ART}m5ytyjy0_pentolo%201.mp4`;
const EV1 = `${ART}1vw0p303_pentolo%202.jpg`;
const EV2 = `${ART}d1k5uj2b_IMG-20260606-WA0013.jpg`;
const EV3 = `${ART}um7udil6_IMG-20250421-WA0003.jpg`;

const NAV = [
  { label: "The Show", id: "v2-show" },
  { label: "The Craft", id: "v2-craft" },
  { label: "Business Models", id: "v2-models" },
  { label: "Grand Events", id: "v2-events" },
  { label: "Gallery", id: "v2-gallery" },
];
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

/* ---------- header ---------- */
const V2Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", on);
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${scrolled ? "bg-white/70 backdrop-blur-xl saturate-150 border-b border-white/50" : "bg-transparent"}`}
      data-testid="v2-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="v2-serif text-3xl md:text-4xl tracking-tight text-[#1b2431]" data-testid="v2-logo">
          Filata<span className="text-[#1d5a73]">.</span>
        </button>
        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((l) => (
            <button key={l.id} onClick={() => scrollTo(l.id)} data-testid={`v2-nav-${l.id}`} className="text-sm text-[#1b2431]/80 hover:text-[#1d5a73] transition-colors">
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href="/" data-testid="v2-classic-link" className="hidden sm:inline-flex text-xs uppercase tracking-[0.15em] text-[#637381] hover:text-[#1d5a73] transition-colors">← Classic</a>
          <button onClick={() => scrollTo("v2-book")} data-testid="v2-header-cta" className="hidden md:inline-flex rounded-full bg-[#1d5a73] px-6 py-3 text-sm text-[#faf8f5] hover:bg-[#1b2431] transition-colors">
            Book a Table
          </button>
          <button onClick={() => setOpen(!open)} className="lg:hidden text-[#1b2431]" data-testid="v2-menu-toggle" aria-label="Menu">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-[#e3d8c8] px-6 py-6 flex flex-col gap-4" data-testid="v2-mobile-menu">
          {NAV.map((l) => (
            <button key={l.id} onClick={() => { scrollTo(l.id); setOpen(false); }} className="text-left text-lg v2-serif text-[#1b2431]">{l.label}</button>
          ))}
          <a href="/" className="text-sm text-[#637381]">← Classic version</a>
        </div>
      )}
    </header>
  );
};

/* ---------- hero ---------- */
const V2Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  return (
    <section ref={ref} className="relative pt-32 md:pt-44 pb-16 md:pb-24 overflow-hidden" data-testid="v2-hero">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-end">
        <div className="lg:col-span-7 relative z-20">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="text-xs uppercase tracking-[0.28em] text-[#1d5a73] mb-6">
            A Mediterranean Ritual · Made by Hand
          </motion.p>
          <h1 className="v2-serif text-[#1b2431] text-6xl md:text-8xl lg:text-9xl leading-[0.86] tracking-tight font-light">
            <motion.span initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }} className="block">Feel the</motion.span>
            <motion.span initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.22 }} className="block italic text-[#1d5a73]">warmth</motion.span>
            <motion.span initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.34 }} className="block">of Italy, live.</motion.span>
          </h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.5 }} className="text-base md:text-lg text-[#637381] max-w-lg mt-8 leading-relaxed">
            Under the same sun that ripens the fields, warm mozzarella is drawn by hand and shared
            while it&rsquo;s still soft. More than a dish — a memory being made, right at your table.
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.62 }} className="flex items-center gap-6 mt-10">
            <button onClick={() => scrollTo("v2-book")} data-testid="v2-hero-cta" className="rounded-full bg-[#1d5a73] px-8 py-4 text-sm text-[#faf8f5] hover:bg-[#1b2431] transition-colors">
              Book a Table
            </button>
            <button onClick={() => scrollTo("v2-craft")} className="text-sm text-[#1b2431] underline underline-offset-4 decoration-[#1d5a73]/40 hover:decoration-[#1d5a73] transition-colors">
              Discover the ritual
            </button>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative z-10">
          <motion.div style={{ y }} className="relative">
            <div className="overflow-hidden rounded-t-[999px] rounded-b-[2rem] aspect-[3/4.2] shadow-[0_40px_80px_-30px_rgba(29,90,115,0.4)]">
              <img src={HERO} alt="Hands stretching fresh mozzarella in the sunlight" className="w-full h-full object-cover" />
            </div>
            <div className="absolute -left-6 md:-left-10 bottom-10 bg-[#faf8f5] rounded-2xl px-5 py-4 shadow-xl border border-[#e3d8c8]">
              <p className="v2-serif italic text-2xl text-[#1d5a73] leading-none">Fresh &middot; Warm</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#637381] mt-1">pulled to order</p>
            </div>
          </motion.div>
        </div>
      </div>
      <Sun className="absolute -top-10 right-10 h-40 w-40 text-[#e3d8c8] opacity-60" strokeWidth={0.75} aria-hidden="true" />
    </section>
  );
};

/* ---------- editorial ribbon ---------- */
const V2Ribbon = () => {
  const words = ["Fatto a mano", "Sotto il sole", "Alla tua tavola", "Con amore", "Tradizione viva"];
  const row = [...words, ...words];
  return (
    <div className="bg-[#e3d8c8] py-8 overflow-hidden" data-testid="v2-ribbon" aria-hidden="true">
      <div className="v2-ribbon-track">
        {row.map((w, i) => (
          <span key={i} className="v2-serif italic text-4xl md:text-6xl text-[#1b2431]/80 mx-12">{w}<span className="text-[#1d5a73] mx-12">✳</span></span>
        ))}
      </div>
    </div>
  );
};

/* ---------- the show (split sticky) ---------- */
const V2Show = () => (
  <section id="v2-show" className="py-20 md:py-32" data-testid="v2-show-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <p className="text-xs uppercase tracking-[0.28em] text-[#1d5a73] mb-6">The Show</p>
          <h2 className="v2-serif text-5xl md:text-6xl leading-[0.95] text-[#1b2431] font-light">
            Nourished by the sun, shaped by <span className="italic text-[#1d5a73]">human hands.</span>
          </h2>
          <p className="text-base md:text-lg text-[#637381] mt-8 leading-relaxed max-w-md">
            No screens, no shortcuts — just warm curd, a pair of skilled hands, and the quiet
            magic of watching mozzarella come to life in front of you. It&rsquo;s theatre you can taste.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-10 border-t border-[#e3d8c8] pt-8 max-w-md">
            {[["100%", "By hand"], ["Live", "At your table"], ["1:1", "For your venue"]].map(([v, l]) => (
              <div key={l}>
                <p className="v2-serif text-3xl text-[#1d5a73]">{v}</p>
                <p className="text-xs text-[#637381] mt-1">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="lg:col-span-7 space-y-6">
        {[[BURRATA, "Burrata, torn warm"], [CAPRESE, "Caprese by the sea"], [STILL, "Fresh from the linen"]].map(([src, cap], i) => (
          <FadeUp key={i} delay={i * 0.05}>
            <figure className="overflow-hidden rounded-[2rem] aspect-[4/3]">
              <img src={src} alt={cap} className="w-full h-full object-cover" />
            </figure>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- the craft (bento) ---------- */
const V2Craft = () => {
  const chapters = [
    { n: "01", t: "The Curd", b: "It begins with the finest milk and a little patience — the curd rests in warm whey until it turns silky and alive.", img: STILL, span: "lg:col-span-7 lg:row-span-2", ratio: "aspect-[4/5] lg:aspect-auto lg:h-full" },
    { n: "02", t: "The Stretch", b: "Drawn and folded by hand — pasta filata. Guests lean in as dull strands turn glossy before their eyes.", img: HERO, span: "lg:col-span-5", ratio: "aspect-[4/3]" },
    { n: "03", t: "The Table", b: "Torn while still warm, dressed with golden oil and sea salt, and shared the way Italy always has.", img: BURRATA, span: "lg:col-span-5", ratio: "aspect-[4/3]" },
  ];
  return (
    <section id="v2-craft" className="py-20 md:py-32 bg-[#f0ece1]" data-testid="v2-craft-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="max-w-2xl mb-14 md:mb-20">
          <p className="text-xs uppercase tracking-[0.28em] text-[#1d5a73] mb-5">The Craft · Pasta Filata</p>
          <h2 className="v2-serif text-5xl md:text-7xl leading-[0.95] text-[#1b2431] font-light">A ritual as old as the <span className="italic text-[#1d5a73]">sun.</span></h2>
        </FadeUp>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:auto-rows-[minmax(0,1fr)]">
          {chapters.map((c, i) => (
            <FadeUp key={c.n} delay={i * 0.08} className={c.span}>
              <div className="h-full bg-[#faf8f5] rounded-[2rem] overflow-hidden flex flex-col">
                <div className={`overflow-hidden ${c.ratio}`}>
                  <img src={c.img} alt={c.t} className="w-full h-full object-cover" />
                </div>
                <div className="p-8 md:p-10">
                  <span className="v2-serif italic text-4xl text-[#1d5a73]/70">{c.n}</span>
                  <h3 className="v2-serif text-3xl text-[#1b2431] mt-2 mb-3">{c.t}</h3>
                  <p className="text-[#637381] leading-relaxed">{c.b}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- business models (cardless architectural) ---------- */
const V2Models = () => {
  const models = [
    { n: "01", t: "Event & Wedding", b: "A tableside spectacle that turns a celebration into a story guests will retell for years.", est: "$1,500–4,000 / event · ~$15k–40k / month at volume" },
    { n: "02", t: "High-End Senior Living", b: "A warm, joyful ritual that brings dignity, freshness and delight to premium residences.", est: "Recurring contracts · ~$25k–35k / month" },
    { n: "03", t: "Full Concept Restaurant", b: "The complete build — the live show at the heart of a warm, sunlit Mediterranean room.", est: "Show + venue · ~$70k–80k / month gross (see P&L)" },
  ];
  return (
    <section id="v2-models" className="py-20 md:py-32 bg-[#e3d8c8]" data-testid="v2-models-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.28em] text-[#1d5a73] mb-5">Business Models</p>
          <h2 className="v2-serif text-5xl md:text-7xl leading-[0.95] text-[#1b2431] font-light">Three ways to share the <span className="italic text-[#1d5a73]">warmth.</span></h2>
        </FadeUp>
        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-[#1b2431]/15">
          {models.map((m, i) => (
            <FadeUp key={m.n} delay={i * 0.1}>
              <div className={`p-8 md:p-12 h-full ${i > 0 ? "md:border-l border-[#1b2431]/15" : ""} border-b md:border-b-0`} data-testid={`v2-model-${i + 1}`}>
                <span className="v2-serif italic text-5xl text-[#1d5a73]/70">{m.n}</span>
                <h3 className="v2-serif text-3xl text-[#1b2431] mt-6 mb-4">{m.t}</h3>
                <p className="text-[#637381] leading-relaxed">{m.b}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- grand events (organic) ---------- */
const V2Events = () => {
  const formats = [
    { Icon: Heart, t: "Luxury Weddings", d: "A theatrical centerpiece your guests will never forget." },
    { Icon: Building2, t: "Corporate Events", d: "A shared, joyful moment that breaks the ice." },
    { Icon: Hotel, t: "Large Resorts", d: "Volume without compromise — cooked and served live." },
  ];
  return (
    <section id="v2-events" className="py-20 md:py-32" data-testid="v2-events-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="max-w-2xl mb-14 md:mb-20">
          <p className="text-xs uppercase tracking-[0.28em] text-[#1d5a73] mb-5">Grand Events &amp; Show Cooking</p>
          <h2 className="v2-serif text-5xl md:text-7xl leading-[0.95] text-[#1b2431] font-light">The show, under the <span className="italic text-[#1d5a73]">open sky.</span></h2>
          <p className="text-base md:text-lg text-[#637381] mt-8 leading-relaxed">
            Beyond the table, the same craft goes large — cooked and served live from the giant pan.
            A format made for weddings, corporate gatherings and resorts, where the cooking itself
            becomes the celebration.
          </p>
        </FadeUp>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 items-center">
          <FadeUp>
            <div className="relative overflow-hidden rounded-[3rem] aspect-[4/5]">
              <video data-testid="v2-events-video" src={EV_VIDEO} poster="/stage/poster-pentolo.jpg" autoPlay muted loop playsInline className="w-full h-full object-cover" />
            </div>
          </FadeUp>
          <div className="grid grid-cols-2 gap-6">
            {[EV1, EV2, EV3, SEA1].map((src, i) => (
              <FadeUp key={i} delay={i * 0.07}>
                <figure className={`overflow-hidden aspect-square ${i % 2 === 0 ? "rounded-[999px]" : "rounded-[2rem]"}`}>
                  <img src={src} alt="Show cooking on location" className="w-full h-full object-cover" />
                </figure>
              </FadeUp>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16 border-t border-[#e3d8c8] pt-12">
          {formats.map((f) => (
            <FadeUp key={f.t}>
              <f.Icon className="h-7 w-7 text-[#1d5a73] mb-4" strokeWidth={1.5} />
              <h3 className="v2-serif text-2xl md:text-3xl text-[#1b2431] mb-2">{f.t}</h3>
              <p className="text-[#637381] leading-relaxed max-w-xs">{f.d}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- mediterranean stage (evening chapter) ---------- */
const V2Stage = () => {
  const shots = [
    { src: `${ART}kufvnmtm_IMG_3460.webp`, cap: "The casaro lifts the mozzarella, live among the palms" },
    { src: `${ART}utuw9aq7_IMG_3485.webp`, cap: "Each knot tied by hand, moments before serving" },
  ];
  const plates = [
    { src: `${ART}7hdtdaxq_IMG_3561.webp`, cap: "Almond-crusted tartare & burrata" },
    { src: `${ART}dqs63amu_IMG_3568.webp`, cap: "Crispy prawn, avocado & physalis" },
    { src: `${ART}8fozgpe7_IMG_3577.webp`, cap: "The tasting, course after course" },
  ];
  return (
    <section id="v2-stage" className="py-20 md:py-32" data-testid="v2-stage-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="max-w-2xl mb-14 md:mb-20">
          <p className="text-xs uppercase tracking-[0.28em] text-[#1d5a73] mb-5">The Mediterranean Style</p>
          <h2 className="v2-serif text-5xl md:text-7xl leading-[0.95] text-[#1b2431] font-light">
            And when the sun sets, the <span className="italic text-[#1d5a73]">show begins.</span>
          </h2>
          <p className="text-base md:text-lg text-[#637381] mt-8 leading-relaxed">
            Real footage from one warm evening — the casaro at work in the open air, steam rising
            from the pot, guests drawn close as milk becomes mozzarella under the stars.
          </p>
        </FadeUp>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
          <FadeUp className="lg:col-span-5">
            <figure className="overflow-hidden rounded-t-[999px] rounded-b-[2rem] aspect-[3/4] lg:aspect-auto lg:h-full shadow-[0_40px_80px_-30px_rgba(27,36,49,0.5)]">
              <img src="/stage/carlo-curd.webp" alt="Carlo, maestro casaro for three generations — the curd comes alive between his hands" data-testid="v2-stage-main-photo" className="w-full h-full object-cover" />
            </figure>
          </FadeUp>
          <div className="lg:col-span-7 grid grid-cols-2 gap-6">
            {shots.map((s) => (
              <FadeUp key={s.src}>
                <figure className="overflow-hidden rounded-[2rem] aspect-[4/3]">
                  <img src={s.src} alt={s.cap} className="w-full h-full object-cover" />
                </figure>
              </FadeUp>
            ))}
            <FadeUp className="col-span-2">
              <figure className="relative overflow-hidden rounded-[2rem] aspect-[16/8]">
                <video src={`${ART}qosqkaii_WhatsApp%20Video%202026-08-29%20at%2018.50.25.mp4`} poster="/stage/poster-stretch.jpg" autoPlay muted loop playsInline data-testid="v2-stage-video" className="absolute inset-0 w-full h-full object-cover" />
                <figcaption className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#1b2431]/70 to-transparent">
                  <span className="v2-serif italic text-[#faf8f5] text-lg md:text-xl">The stretch, live from the pot</span>
                </figcaption>
              </figure>
            </FadeUp>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          {plates.map((p) => (
            <FadeUp key={p.src}>
              <figure className="overflow-hidden rounded-[1.5rem] aspect-[4/3] relative">
                <img src={p.src} alt={p.cap} className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700" />
                <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-[#1b2431]/70 to-transparent">
                  <span className="v2-serif italic text-[#faf8f5] text-base">{p.cap}</span>
                </figcaption>
              </figure>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- gallery (masonry with padding) ---------- */
const V2Gallery = () => {
  const shots = [BURRATA, SEA2, CAPRESE, STILL, SEA3, HERO];
  return (
    <section id="v2-gallery" className="py-20 md:py-32 bg-[#f0ece1]" data-testid="v2-gallery-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
          <h2 className="v2-serif text-5xl md:text-7xl leading-[0.95] text-[#1b2431] font-light max-w-xl">A table full of <span className="italic text-[#1d5a73]">sunshine.</span></h2>
          <div className="max-w-xs">
            <p className="text-sm text-[#637381]">Bright, generous, handmade — the Filata way, in the light of day.</p>
            <p className="text-xs text-[#637381]/70 italic mt-3">Burrata — born in Puglia, the heart of southern Italy.</p>
          </div>
        </FadeUp>
        <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
          {shots.map((src, i) => (
            <figure key={i} className="mb-6 break-inside-avoid overflow-hidden rounded-[1.5rem]">
              <img src={src} alt="Filata plated" className="w-full object-cover hover:scale-[1.03] transition-transform duration-700" />
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- enquiry form ---------- */
const eventTypes = ["Book a Table", "Event & Wedding", "Corporate Event", "Large Resort", "Press & Media"];
const V2Enquiry = () => {
  const [form, setForm] = useState({ name: "", email: "", type: eventTypes[0], details: "" });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/waitlist`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("failed");
      toast.success("Request received", { description: `Grazie, ${form.name.split(" ")[0]}. We'll be in touch very soon.` });
      setForm({ name: "", email: "", type: eventTypes[0], details: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
  };
  const input = "w-full bg-[#faf8f5] border border-[#e3d8c8] rounded-xl px-4 py-3.5 text-[#1b2431] placeholder:text-[#637381]/60 focus:outline-none focus:border-[#1d5a73] focus:ring-2 focus:ring-[#1d5a73]/20 transition-colors";
  return (
    <section id="v2-book" className="py-20 md:py-32 bg-[#e3d8c8]" data-testid="v2-contact-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-6">
          <p className="text-xs uppercase tracking-[0.28em] text-[#1d5a73] mb-6">Let&rsquo;s Talk</p>
          <h2 className="v2-serif text-5xl md:text-7xl leading-[0.92] text-[#1b2431] font-light">
            Bring the <span className="italic text-[#1d5a73]">warmth</span> to your venue.
          </h2>
          <p className="text-base md:text-lg text-[#637381] mt-8 max-w-md leading-relaxed">
            Tell us a little about your venue or occasion, and we&rsquo;ll arrange a private
            presentation and tasting — made just for you.
          </p>
          <div className="mt-10 space-y-2 text-sm text-[#637381]">
            <p>Private events · Bespoke catering · Full-concept venues</p>
            <p>Available for select partners worldwide</p>
          </div>
        </div>
        <form onSubmit={submit} className="lg:col-span-6 space-y-5" data-testid="v2-enquiry-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <input data-testid="v2-input-name" className={input} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="v2-input-email" type="email" className={input} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <select data-testid="v2-select-type" className={input} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
            {eventTypes.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <textarea data-testid="v2-input-details" rows={4} className={input} placeholder="Tell us about your venue or occasion…" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} />
          <button type="submit" data-testid="v2-enquiry-submit-button" className="inline-flex items-center gap-2 rounded-full bg-[#1d5a73] px-8 py-4 text-sm text-[#faf8f5] hover:bg-[#1b2431] transition-colors">
            Request a Presentation <ArrowUpRight className="h-4 w-4" />
          </button>
        </form>
      </div>
    </section>
  );
};

/* ---------- footer ---------- */
const V2Footer = () => (
  <footer className="bg-[#1b2431] text-[#faf8f5]" data-testid="v2-footer">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
      <div>
        <p className="v2-serif text-4xl md:text-5xl">Filata<span className="text-[#7fb0c4]">.</span></p>
        <p className="text-sm text-[#faf8f5]/50 mt-3">Live Mozzarella Experience — Mediterranean by day</p>
      </div>
      <div className="flex flex-wrap gap-6 text-sm text-[#faf8f5]/60">
        {NAV.map((l) => <button key={l.id} onClick={() => scrollTo(l.id)} className="hover:text-[#faf8f5] transition-colors">{l.label}</button>)}
        <a href="/" className="hover:text-[#7fb0c4] transition-colors">Classic version</a>
      </div>
      <p className="text-xs text-[#faf8f5]/40">© {new Date().getFullYear()} Filata — Live Mozzarella Experience.</p>
    </div>
  </footer>
);

export default function V2Landing() {
  return (
    <div className="v2 min-h-screen" data-testid="v2-landing">
      <div className="noise-overlay" aria-hidden="true" />
      <SmoothScroll>
        <V2Header />
        <main>
          <V2Hero />
          <V2Ribbon />
          <V2Show />
          <V2Craft />
          <V2Models />
          <V2Events />
          <V2Stage />
          <V2Gallery />
          <V2Enquiry />
        </main>
        <V2Footer />
      </SmoothScroll>
    </div>
  );
}
